import type { Dayjs } from 'dayjs';
import type { DayGroup } from '@nexus/ari-lib/sections/file-library';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import { groupByDay as groupItemsByDay } from '@nexus/ari-lib/sections/file-library';

import { DOCUMENT_KINDS } from './documents-api-types';

import type {
  DocumentKind,
  LibraryEntry,
  LibrarySort,
  FolderRecord,
  NotReadReason,
  ProcessingState,
} from './documents-api-types';

export type DocumentType = DocumentKind;

export type DocumentItem = {
  id: string;
  type: DocumentType;
  name: string;
  mimeType: string | null;
  sizeBytes: number;
  folderId: string | null;
  createdAt: string;
  modifiedAt: string;
  deletedAt: string | null;
  daysLeft: number | null;
  previewUrl: string | null;
  processingState: ProcessingState | null;
  notReadReason: NotReadReason | null;
  replacing: boolean;
  hasPreviewPdf: boolean;
  pageAspect: number | null;
  pageCount: number | null;
  imagesOnlyPages: number;
  usedAt: string | null;
};

export type DocumentFolder = FolderRecord;

export type FolderEntry = DocumentFolder & {
  itemCount: number;
  deletedAt: string | null;
  daysLeft: number | null;
};

export type DocumentBucket = 'recent' | 'folders' | 'bin';

export type DocumentFilters = {
  bucket: DocumentBucket;
  folderId?: string | null;
};

export type DocumentTypeFilter = 'all' | 'folders' | DocumentType;

export type DocumentsSortOption = 'lastOpened' | 'alphabetical' | 'dateCreated' | 'lastModified' | 'dateDeleted';
/** `asc` is "Oldest first" or "A to Z". `desc` is "Newest first" or "Z to A". */
export type DocumentsSortDirection = 'asc' | 'desc';

/** The bin sorts by the date of the delete. Recents sorts by the last use, which only it knows. CLOUD-1094. */
export function sortOptionsFor(bucket: DocumentBucket): DocumentsSortOption[] {
  if (bucket === 'bin') return ['alphabetical', 'dateDeleted'];
  if (bucket === 'recent') return ['lastOpened', 'alphabetical', 'dateCreated', 'lastModified'];
  return ['alphabetical', 'dateCreated', 'lastModified'];
}

export type DocumentsSort = { option: DocumentsSortOption; direction: DocumentsSortDirection };

const DEFAULT_OPTION: Record<DocumentBucket, DocumentsSortOption> = {
  folders: 'lastModified',
  recent: 'lastOpened',
  bin: 'dateDeleted',
};

export function effectiveSortOption(bucket: DocumentBucket, option: DocumentsSortOption): DocumentsSortOption {
  return sortOptionsFor(bucket).includes(option) ? option : DEFAULT_OPTION[bucket];
}

/** A name sorts A to Z first. A date sorts the newest first. */
export function defaultSortDirection(option: DocumentsSortOption): DocumentsSortDirection {
  return option === 'alphabetical' ? 'asc' : 'desc';
}

export function defaultSortFor(bucket: DocumentBucket): DocumentsSort {
  const option = DEFAULT_OPTION[bucket];
  return { option, direction: defaultSortDirection(option) };
}

export type DocumentDayGroup = DayGroup<DocumentItem>;

export const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024 * 1024;

export const SORT_PARAM: Record<DocumentsSortOption, LibrarySort> = {
  alphabetical: 'name',
  dateCreated: 'created',
  lastModified: 'modified',
  // The library orders the bin by the delete date when the sort is not by name.
  dateDeleted: 'modified',
  lastOpened: 'used',
};

export function toDocumentType(kind: string | null): DocumentType {
  return DOCUMENT_KINDS.find((known) => known === kind) ?? 'other';
}

export function toDocumentItem(entry: LibraryEntry): DocumentItem {
  return {
    id: entry.id,
    type: toDocumentType(entry.kind),
    name: entry.name,
    mimeType: entry.mimeType,
    sizeBytes: entry.byteSize ?? 0,
    folderId: entry.parentId,
    createdAt: entry.createdAt,
    modifiedAt: entry.modifiedAt,
    deletedAt: entry.deletedAt,
    daysLeft: entry.daysLeft,
    previewUrl: entry.previewUrl,
    processingState: entry.processingState ?? null,
    notReadReason: entry.notReadReason ?? null,
    replacing: entry.replacing ?? false,
    hasPreviewPdf: entry.hasPreviewPdf ?? false,
    pageAspect: entry.pageAspect ?? null,
    pageCount: entry.pageCount ?? null,
    imagesOnlyPages: entry.imagesOnlyPages ?? 0,
    usedAt: entry.usedAt ?? null,
  };
}

export function toFolderEntry(entry: LibraryEntry): FolderEntry {
  return {
    id: entry.id,
    name: entry.name,
    parentId: entry.parentId,
    createdAt: entry.createdAt,
    itemCount: entry.childCount,
    deletedAt: entry.deletedAt,
    daysLeft: entry.daysLeft,
  };
}

dayjs.extend(relativeTime);

const VIEWABLE = new Set<DocumentType>(['pdf', 'image', 'video', 'audio', 'text']);

export function isViewable(item: DocumentItem): boolean {
  return VIEWABLE.has(item.type);
}

const MAX_NAME_CHARS = 255;
const CONTROL_CHAR = /\p{Cc}/u;
const CONTROL_CHARS = /\p{Cc}/gu;

/** The documents function's name rule. The dialog refuses a name the function would refuse. */
export function nameProblem(name: string): string | null {
  if (/[/\\]/.test(name)) return 'A name cannot have a slash.';
  if (CONTROL_CHAR.test(name)) return 'A name cannot have a control character.';
  if (name === '.' || name === '..') return 'Use a different name.';
  if (name.normalize('NFC').length > MAX_NAME_CHARS) return `A name can have ${MAX_NAME_CHARS} characters at most.`;
  return null;
}

/** A file name the documents function takes, such as "Icon\r" from macOS or one that starts with a space. */
export function uploadName(fileName: string): string {
  const name = fileName.normalize('NFC').replace(CONTROL_CHARS, '').replace(/[/\\]/g, '_').trim();
  if (!name || name === '.' || name === '..') return 'Untitled';
  if (name.length <= MAX_NAME_CHARS) return name;
  const dot = name.lastIndexOf('.');
  const extension = dot > 0 && name.length - dot <= 16 ? name.slice(dot) : '';
  return name.slice(0, MAX_NAME_CHARS - extension.length).trimEnd() + extension;
}

export function openedLabel(usedAt: string, now: Dayjs = dayjs()): string {
  // The caption is English, and the server clock can be ahead of the browser.
  const used = dayjs(usedAt).locale('en');
  return `Opened ${(used.isAfter(now) ? now.locale('en') : used).from(now)}`;
}

export function daysLeftLabel(daysLeft: number): string {
  return daysLeft === 1 ? '1 day left' : `${daysLeft} days left`;
}

/** Root first, down to `folderId`. Empty at the top level. */
export function getFolderPath(folders: DocumentFolder[], folderId: string | null): DocumentFolder[] {
  const byId = new Map(folders.map((f) => [f.id, f]));
  const path: DocumentFolder[] = [];
  const seen = new Set<string>();

  let current = folderId ? byId.get(folderId) : undefined;
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    path.unshift(current);
    current = current.parentId ? byId.get(current.parentId) : undefined;
  }

  return path;
}

/** The folder and every folder below it. */
export function getFolderSubtree(folders: DocumentFolder[], folderId: string): Set<string> {
  const subtree = new Set<string>([folderId]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const f of folders) {
      if (f.parentId && subtree.has(f.parentId) && !subtree.has(f.id)) {
        subtree.add(f.id);
        changed = true;
      }
    }
  }
  return subtree;
}

export type GroupField = 'createdAt' | 'modifiedAt' | 'deletedAt' | 'usedAt';

export function groupByDay(items: DocumentItem[], field: GroupField, now: Dayjs = dayjs()): DocumentDayGroup[] {
  return groupItemsByDay(items, (item) => item[field] ?? item.modifiedAt, now);
}
